import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  GeneratedReport,
  GeneratedReportsListResponse,
  CreateReportRequest,
  ReportDownloadResponse,
  ScheduledReport,
  ScheduledReportsListResponse,
  CreateScheduledReportRequest,
  UpdateScheduledReportRequest,
} from '@/pages/Reports/types'

// ============= Generated Reports API Functions =============

async function listReports(params?: { limit?: number; offset?: number }): Promise<GeneratedReportsListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
  const query = searchParams.toString()
  return fetchWithAuth(`/api/v1/reports${query ? `?${query}` : ''}`)
}

async function getReport(reportId: string): Promise<GeneratedReport> {
  return fetchWithAuth(`/api/v1/reports/${reportId}`)
}

async function createReport(data: CreateReportRequest): Promise<GeneratedReport> {
  return fetchWithAuth('/api/v1/reports', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function getReportDownloadUrl(reportId: string): Promise<ReportDownloadResponse> {
  return fetchWithAuth(`/api/v1/reports/${reportId}/download`)
}

async function deleteReport(reportId: string): Promise<void> {
  return fetchWithAuth(`/api/v1/reports/${reportId}`, {
    method: 'DELETE',
  })
}

// ============= Scheduled Reports API Functions =============

async function listScheduledReports(params?: { limit?: number; offset?: number }): Promise<ScheduledReportsListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
  const query = searchParams.toString()
  return fetchWithAuth(`/api/v1/report-schedules${query ? `?${query}` : ''}`)
}

async function getScheduledReport(scheduleId: string): Promise<ScheduledReport> {
  return fetchWithAuth(`/api/v1/report-schedules/${scheduleId}`)
}

async function createScheduledReport(data: CreateScheduledReportRequest): Promise<ScheduledReport> {
  return fetchWithAuth('/api/v1/report-schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function updateScheduledReport(scheduleId: string, data: UpdateScheduledReportRequest): Promise<ScheduledReport> {
  return fetchWithAuth(`/api/v1/report-schedules/${scheduleId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

async function pauseScheduledReport(scheduleId: string): Promise<ScheduledReport> {
  return fetchWithAuth(`/api/v1/report-schedules/${scheduleId}/pause`, {
    method: 'POST',
  })
}

async function resumeScheduledReport(scheduleId: string): Promise<ScheduledReport> {
  return fetchWithAuth(`/api/v1/report-schedules/${scheduleId}/resume`, {
    method: 'POST',
  })
}

async function deleteScheduledReport(scheduleId: string): Promise<void> {
  return fetchWithAuth(`/api/v1/report-schedules/${scheduleId}`, {
    method: 'DELETE',
  })
}

// ============= Generated Reports Hooks =============

export function useReports(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['reports', params],
    queryFn: () => listReports(params),
  })
}

export function useReport(reportId: string | null) {
  return useQuery({
    queryKey: ['reports', reportId],
    queryFn: () => getReport(reportId!),
    enabled: !!reportId,
  })
}

export function useCreateReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })
}

export function useReportDownloadUrl() {
  return useMutation({
    mutationFn: getReportDownloadUrl,
  })
}

export function useDeleteReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })
}

// ============= Scheduled Reports Hooks =============

export function useScheduledReports(params?: { limit?: number; offset?: number }, enabled: boolean = true) {
  return useQuery({
    queryKey: ['scheduled-reports', params],
    queryFn: () => listScheduledReports(params),
    enabled,
  })
}

export function useScheduledReport(scheduleId: string | null) {
  return useQuery({
    queryKey: ['scheduled-reports', scheduleId],
    queryFn: () => getScheduledReport(scheduleId!),
    enabled: !!scheduleId,
  })
}

export function useCreateScheduledReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createScheduledReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] })
    },
  })
}

export function useUpdateScheduledReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: string; data: UpdateScheduledReportRequest }) =>
      updateScheduledReport(scheduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] })
    },
  })
}

export function usePauseScheduledReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: pauseScheduledReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] })
    },
  })
}

export function useResumeScheduledReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resumeScheduledReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] })
    },
  })
}

export function useDeleteScheduledReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteScheduledReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] })
    },
  })
}
